<template>
	<SearchListLayout
		ref="layout"
		class="d-publication-list"
		v-bind="{
			...$attrs,
			headerGraphic: 3,
			searchArea: {
				singular: 'udgivelse',
				plural: 'udgivelser',
			},
			searchConfig: {
				limit: 24,
			},
			defaults: getDefaults({
				text: '',
				includeWords: '',
				includePhrase: '',
				excludeWords: '',
				areas: '',
				types: '',
				employees: '',
				languages: '',
			}),
		}"
	>
		<template #searchFields="{ fieldsByName, action, hasExtendedFilters }">
			<div
				class="
					d-publication-list__search-fields
					grid grid-cols-12
					gap-x-3xs/h gap-y-2xs/v
					>=1024:mt-8
					pt-lg/v
					pb-sm/v
				"
			>
				<div class="col-span-12">
					<SearchTextInput
						v-if="fieldsByName.text"
						v-bind="{
							...fieldsByName.text,
							type: 'search',
						}"
						v-model="tempSearchValue.text"
						@keyup.native.enter="
							() => submitText(fieldsByName, action.submit)
						"
					>
						<BaseButton
							aria-label="Udfør søgning"
							class="
								w-48
								>=768:w-52
								h-48
								>=768:h-52
								p-0
								rounded-full
							"
							@click="
								() => submitText(fieldsByName, action.submit)
							"
						>
							<SvgIconSearch class="w-18 h-18" />
						</BaseButton>

						<!-- Toggle for expanded fields -->
						<AccordionHeader
							v-if="hasExtendedFilters"
							id="d-publication-list__expand-toggle"
							tag="BaseButton"
							aria-controls="d-publication-list__expanded-fields"
							aria-label="Avancerede søgefelter"
							class="
								w-48
								>=768:w-auto
								h-48
								>=768:h-52
								<768:p-0
								py-0
								border border-theme
							"
							:class="{
								'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme':
									!(
										$refs.expandedFieldsPanel &&
										$refs.expandedFieldsPanel.isOpen
									),
							}"
						>
							<SvgIconSettings
								class="w-20 >=768:w-25 h-20 >=768:h-25"
							/>
							<SvgIconWedge
								class="
									hidden
									>=768:block
									ml-2xs/h
									w-16
									h-10
									transform
									translate-y-1
									transition-transform
									duration-200
									ease-in-out
								"
								:class="{
									'-rotate-180':
										$refs.expandedFieldsPanel &&
										$refs.expandedFieldsPanel.isOpen,
								}"
							/>
						</AccordionHeader>
					</SearchTextInput>

					<!-- Expanded fields -->
					<AccordionPanel
						v-if="hasExtendedFilters"
						id="d-publication-list__expanded-fields"
						ref="expandedFieldsPanel"
						:transition="{ name: 't-accordion' }"
					>
						<div
							class="
								pt-8
								>=768:pt-2xs/v
								grid grid-cols-12
								gap-8
								>=768:gap-x-3xs/h >=768:gap-y-2xs/v
							"
						>
							<!-- includePhrase -->
							<SearchTextInput
								v-if="'includePhrase' in fieldsByName"
								v-bind="{
									...fieldsByName.includePhrase,
									description: null,
								}"
								v-model="tempSearchValue.includePhrase"
								:aria-describedby="
									fieldsByName.includePhrase.description
										? 'd-publication-list__info--includePhrase'
										: null
								"
								class="col-span-full"
								dimmed
								@keyup.native.enter="
									() =>
										submitText(fieldsByName, action.submit)
								"
							>
								<InfoButton
									v-if="
										fieldsByName.includePhrase.description
									"
									id="d-publication-list__info--includePhrase"
									class="mr-8 >=1024:mr-12"
									:description="
										fieldsByName.includePhrase.description
									"
								/>
							</SearchTextInput>

							<!-- includeWords -->
							<SearchTextInput
								v-if="'includeWords' in fieldsByName"
								v-bind="{
									...fieldsByName.includeWords,
									description: null,
								}"
								v-model="tempSearchValue.includeWords"
								:aria-describedby="
									fieldsByName.includeWords.description
										? 'd-publication-list__info--includeWords'
										: null
								"
								class="col-span-full"
								dimmed
								@keyup.native.enter="
									() =>
										submitText(fieldsByName, action.submit)
								"
							>
								<InfoButton
									v-if="fieldsByName.includeWords.description"
									id="d-publication-list__info--includeWords"
									class="mr-8 >=1024:mr-12"
									:description="
										fieldsByName.includeWords.description
									"
								/>
							</SearchTextInput>

							<!-- excludeWords -->
							<SearchTextInput
								v-if="'excludeWords' in fieldsByName"
								v-bind="{
									...fieldsByName.excludeWords,
									description: null,
								}"
								v-model="tempSearchValue.excludeWords"
								:aria-describedby="
									fieldsByName.excludeWords.description
										? 'd-publication-list__info--excludeWords'
										: null
								"
								class="col-span-full"
								dimmed
								@keyup.native.enter="
									() =>
										submitText(fieldsByName, action.submit)
								"
							>
								<InfoButton
									v-if="fieldsByName.excludeWords.description"
									id="d-publication-list__info--excludeWords"
									class="mr-8 >=1024:mr-12"
									:description="
										fieldsByName.excludeWords.description
									"
								/>
							</SearchTextInput>
						</div>
					</AccordionPanel>
				</div>

				<!-- Row of dropdowns -->
				<div
					class="
						col-span-12
						grid
						>=768:contents
						grid-cols-12
						gap-8
						>=768:gap-x-3xs/h >=768:gap-y-2xs/v
					"
				>
					<SearchDropdown
						v-if="fieldsByName.areas"
						v-bind="fieldsByName.areas"
						v-model="fieldsByName.areas.value"
						class="col-span-12 >=768:col-span-6"
						:class="{
							'>=1200:col-span-4':
								(!!fieldsByName.areas +
									!!fieldsByName.types +
									!!fieldsByName.employees +
									!!fieldsByName.languages) %
								2,
						}"
						show-icon
						@input="() => action.submit()"
					/>

					<SearchDropdown
						v-if="fieldsByName.employees"
						v-bind="fieldsByName.employees"
						v-model="fieldsByName.employees.value"
						class="col-span-12 >=768:col-span-6"
						:class="{
							'>=1200:col-span-4':
								(!!fieldsByName.areas +
									!!fieldsByName.types +
									!!fieldsByName.employees +
									!!fieldsByName.languages) %
								2,
						}"
						@input="() => action.submit()"
					/>

					<SearchDropdown
						v-if="fieldsByName.types"
						v-bind="fieldsByName.types"
						v-model="fieldsByName.types.value"
						class="col-span-12 >=768:col-span-6"
						:class="{
							'>=1200:col-span-4':
								(!!fieldsByName.areas +
									!!fieldsByName.types +
									!!fieldsByName.employees +
									!!fieldsByName.languages) %
								2,
						}"
						@input="() => action.submit()"
					/>

					<SearchDropdown
						v-if="fieldsByName.languages"
						v-bind="fieldsByName.languages"
						v-model="fieldsByName.languages.value"
						class="col-span-12 >=768:col-span-6"
						:class="{
							'>=1200:col-span-4':
								(!!fieldsByName.areas +
									!!fieldsByName.types +
									!!fieldsByName.employees +
									!!fieldsByName.languages) %
								2,
						}"
						@input="() => action.submit()"
					/>
				</div>
			</div>
		</template>
	</SearchListLayout>
</template>

<script>
import SearchListLayout from '~/components/main/SearchListLayout';
import SearchTextInput from '~/components/main/SearchTextInput';
import SearchDropdown from '~/components/main/SearchDropdown';
import InfoButton from '~/components/main/InfoButton';
import { AccordionHeader, AccordionPanel } from '~/components/shared/Accordion';
import SvgIconSearch from '~/assets/svgs/icon-search.svg?inline';
import SvgIconSettings from '~/assets/svgs/icon-settings.svg?inline';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg?inline';

export default {
	name: 'MainPublicationList',

	components: {
		SearchListLayout,
		SearchTextInput,
		SearchDropdown,
		InfoButton,
		AccordionHeader,
		AccordionPanel,
		SvgIconSearch,
		SvgIconSettings,
		SvgIconWedge,
	},
	inheritAttrs: false,

	data() {
		const defaults = this.getDefaults();
		return {
			tempSearchValue: {
				text:
					'text' in this.$route.query
						? this.$route.query.text ?? ''
						: defaults.text ?? '',
				includePhrase:
					'includePhrase' in this.$route.query
						? this.$route.query.includePhrase ?? ''
						: defaults.includePhrase ?? '',
				includeWords:
					'includeWords' in this.$route.query
						? this.$route.query.includeWords ?? ''
						: defaults.includeWords ?? '',
				excludeWords:
					'excludeWords' in this.$route.query
						? this.$route.query.excludeWords ?? ''
						: defaults.excludeWords ?? '',
			},
		};
	},

	methods: {
		getDefaults(obj = {}) {
			const { fields = [] } = this.$attrs.search || {};
			const newObj = fields
				.filter((field) => 'defaultValue' in field)
				.reduce((acc, field) => {
					acc[field.name] = field.defaultValue;
					return acc;
				}, obj);
			return { ...obj, ...newObj };
		},
		submitText(fieldsByName, submit) {
			let changed = false;

			if (
				'text' in fieldsByName &&
				fieldsByName.text.value !== this.tempSearchValue.text
			) {
				fieldsByName.text.value = this.tempSearchValue.text;
				changed = true;
			}
			if (
				'includePhrase' in fieldsByName &&
				fieldsByName.includePhrase.value !==
					this.tempSearchValue.includePhrase
			) {
				fieldsByName.includePhrase.value =
					this.tempSearchValue.includePhrase;
				changed = true;
			}
			if (
				'includeWords' in fieldsByName &&
				fieldsByName.includeWords.value !==
					this.tempSearchValue.includeWords
			) {
				fieldsByName.includeWords.value =
					this.tempSearchValue.includeWords;
				changed = true;
			}
			if (
				'excludeWords' in fieldsByName &&
				fieldsByName.excludeWords.value !==
					this.tempSearchValue.excludeWords
			) {
				fieldsByName.excludeWords.value =
					this.tempSearchValue.excludeWords;
				changed = true;
			}

			changed && submit();
		},
	},
};
</script>
